import { useEffect, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import WorkTemplatesContext from '@atom/components/workTemplates/WorkTemplatesContext';
import { useWorkTemplatesView } from '@atom/hooks/useWorkTemplatesView';
import { Progress } from '@atom/mui';
import { PolicyAction } from '@atom/types/policy';
import api from '@atom/utilities/api';
import { WORK_TEMPLATES_FOLDER_TREE_ENDPOINT } from '@atom/utilities/endpoints';

import WorkTemplatesFolderTree from './workTemplatesFolderTree/WorkTemplatesFolderTree';
import WorkTemplatesFolderList from './workTemplatesList/WorkTemplatesFolderList';
import WorkTemplatesList from './workTemplatesList/WorkTemplatesList';
import WorkTemplatesHeader from './WorkTemplatesHeader';

import classes from './workTemplates.module.css';

const styles = {
  progress: {
    width: '100%',
  },
};

const WorkTemplates = () => {
  const [isActiveFolderLeafNode, setIsActiveFolderLeafNode] =
    useState<boolean>(false);

  const { setWorkTemplatesView, workTemplatesView } = useWorkTemplatesView();
  const { activeFolderId, loading, open, workTemplateFolderTree, page, limit } =
    workTemplatesView;

  const setActiveFolderId = (value: string) => {
    setWorkTemplatesView({ activeFolderId: value });
  };

  const refetchWorkTemplateFolderTree = async () => {
    const { data } = await api.get(WORK_TEMPLATES_FOLDER_TREE_ENDPOINT, {
      action: PolicyAction.READ,
      countParentWorkTemplates: true,
    });
    return setWorkTemplatesView({ workTemplateFolderTree: data });
  };

  useEffect(() => {
    const getWorkTemplateFolderTree = async () => {
      setWorkTemplatesView({ loading: true });

      const { data } = await api.get(WORK_TEMPLATES_FOLDER_TREE_ENDPOINT, {
        action: PolicyAction.READ,
        countParentWorkTemplates: true,
      });

      setWorkTemplatesView({
        workTemplateFolderTree: data,
        loading: false,
      });
      setIsActiveFolderLeafNode(R.isEmpty(data.children));
    };

    if (R.isEmpty(workTemplateFolderTree)) {
      getWorkTemplateFolderTree();
    }
  }, []);

  const toggleOpen = (id: string) => {
    if (open.has(id)) {
      open.delete(id);
      setWorkTemplatesView({ open: new Set([...open]) });
    } else {
      setWorkTemplatesView({ open: new Set([...open.add(id)]) });
    }
  };

  return (
    <WorkTemplatesContext.Provider
      value={{
        activeFolderId,
        setActiveFolderId,
        open,
        toggleOpen,
        refetchWorkTemplateFolderTree,
        isActiveFolderLeafNode,
        setIsActiveFolderLeafNode,
        page,
        limit,
        setWorkTemplatesView,
      }}
    >
      <WorkTemplatesHeader />
      <div className={classes.portalContainer}>
        {loading ? (
          <Progress style={styles.progress} />
        ) : (
          <>
            <div className={clsx(classes.pane, classes.left)}>
              <WorkTemplatesFolderTree
                workTemplateFolderTree={workTemplateFolderTree}
              />
            </div>
            <div className={clsx(classes.pane, classes.right)}>
              {isActiveFolderLeafNode ? (
                <WorkTemplatesList />
              ) : (
                <WorkTemplatesFolderList
                  workTemplateFolderTree={workTemplateFolderTree}
                />
              )}
            </div>
          </>
        )}
      </div>
    </WorkTemplatesContext.Provider>
  );
};

export default WorkTemplates;
